import { Link } from 'react-router-dom';
import { ArrowRight, CalendarCheck, ShoppingBag } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { StatusPill } from '@/components/orders/StatusPill';
import { useOrders } from '@/hooks/useOrders';
import { useShopPath } from '@/hooks/useShopPath';

/**
 * Order activity for the Product Reservations and Local Checkout manage
 * pages. Both are order surfaces rather than feed channels — in place of the
 * performance section they show their orders, sourced from the same orders
 * API the Orders page uses, with links through to manage them there.
 */

// Statuses where the order still needs the retailer to act on it.
const AWAITING = new Set(['reserved', 'paid', 'confirmed']);

const CONFIG = {
	'product-reservations': {
		orderType: 'reservation',
		title: 'Reservations',
		description: 'Customers reserving your in-store stock to collect and pay on the spot.',
		countLabel: 'Reservations',
		recentTitle: 'Recent reservations',
		emptyIcon: CalendarCheck,
		emptyTitle: 'No reservations yet',
		emptyBody: 'When a local shopper reserves an item, it appears here and in your Orders.'
	},
	'local-checkout': {
		orderType: 'click-collect',
		title: 'Click & Collect',
		description: 'Customers buying online and collecting in store.',
		countLabel: 'Orders',
		recentTitle: 'Recent orders',
		emptyIcon: ShoppingBag,
		emptyTitle: 'No orders yet',
		emptyBody: 'When a local shopper buys online to collect in store, it appears here and in your Orders.'
	}
};

function formatRelativeDate(value) {
	if (!value) return null;
	const ms = typeof value === 'number' ? value : Date.parse(value);
	if (!Number.isFinite(ms)) return null;
	const days = Math.floor((Date.now() - ms) / 86400000);
	if (days <= 0) return 'today';
	if (days === 1) return 'yesterday';
	if (days < 7) return `${days} days ago`;
	return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function productSummary(products) {
	if (!Array.isArray(products) || products.length === 0) return 'Order';
	const [first] = products;
	const name = first.name ?? first.title ?? 'Product';
	return products.length > 1 ? `${name} + ${products.length - 1} more` : name;
}

export function ChannelOrders({ shopId, channelId }) {
	const { orders, isLoading } = useOrders(shopId);
	const { shopPath } = useShopPath();
	const config = CONFIG[channelId];
	if (!config) return null;

	const matching = orders.filter((order) => order.orderType === config.orderType);
	const awaiting = matching.filter((order) => AWAITING.has(order.status)).length;
	const completed = matching.filter((order) => order.status === 'completed').length;
	const recent = [...matching].sort((a, b) => Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0)).slice(0, 5);

	const stats = [
		{ label: config.countLabel, value: matching.length },
		{ label: 'Awaiting action', value: awaiting },
		{ label: 'Completed', value: completed }
	];
	const EmptyIcon = config.emptyIcon;

	return (
		<section className="space-y-4">
			<SoftCard className="space-y-5 !p-5 md:!p-6">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div>
						<h3 className="text-[15px] font-bold text-foreground md:text-base">{config.title}</h3>
						<p className="mt-0.5 text-xs text-muted-foreground">{config.description}</p>
					</div>
					<Link
						to={shopPath('/orders')}
						className="inline-flex items-center gap-1.5 text-sm font-semibold text-action hover:underline"
					>
						Manage in Orders <ArrowRight className="h-3.5 w-3.5" />
					</Link>
				</div>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					{stats.map((stat) => (
						<div
							key={stat.label}
							className="flex flex-col items-center rounded-2xl border border-border bg-card p-5 text-center"
						>
							<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{stat.label}</div>
							<div className="mt-3 text-3xl font-extrabold tracking-tight tabular-nums text-foreground md:text-4xl">
								{isLoading ? '—' : stat.value.toLocaleString('en-GB')}
							</div>
						</div>
					))}
				</div>
			</SoftCard>

			{recent.length > 0 ? (
				<SoftCard className="overflow-hidden !p-0">
					<div className="flex items-center justify-between gap-3 border-b border-border/60 px-5 py-3.5">
						<h4 className="text-sm font-bold text-foreground">{config.recentTitle}</h4>
						<Link to={shopPath('/orders')} className="text-xs font-semibold text-action hover:underline">
							View all
						</Link>
					</div>
					<div className="divide-y divide-border/60">
						{recent.map((order) => (
							<div key={order.id} className="flex items-center gap-4 px-5 py-3.5">
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-semibold text-foreground">{order.customerName ?? 'Customer'}</div>
									<div className="truncate text-xs text-muted-foreground">{productSummary(order.products)}</div>
								</div>
								{order.createdAt ? (
									<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
										{formatRelativeDate(order.createdAt)}
									</span>
								) : null}
								<StatusPill status={order.status} />
							</div>
						))}
					</div>
				</SoftCard>
			) : (
				<SoftCard className="flex flex-col items-center gap-3 !p-8 text-center">
					<div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent">
						<EmptyIcon className="h-5 w-5 text-muted-foreground" />
					</div>
					<div>
						<div className="text-sm font-bold text-foreground">{config.emptyTitle}</div>
						<p className="mt-0.5 text-sm text-muted-foreground">{config.emptyBody}</p>
					</div>
				</SoftCard>
			)}
		</section>
	);
}
